import Keycloak from 'keycloak-js';
import { getKeycloakConfig } from '@/utils/validateEnv';

// Keycloak configuration
const keycloakConfig = getKeycloakConfig();

// Initialize Keycloak instance
const keycloak = new Keycloak(keycloakConfig);

// Initialize Keycloak with configuration
export const initKeycloak = async (): Promise<boolean> => {
  try {
    // Disable checkLoginIframe everywhere — modern browsers block third-party
    // cookies, which causes the iframe session check to falsely detect logout
    // and trigger onAuthLogout before auth flow completes.
    const authenticated = await keycloak.init({
      onLoad: 'check-sso',
      checkLoginIframe: false,
      silentCheckSsoRedirectUri: window.location.origin + '/silent-check-sso.html',
      pkceMethod: 'S256',
    });

    // Set up Keycloak event listeners to watch session changes
    keycloak.onTokenExpired = () => {
      console.log('[Keycloak] Token expired, refreshing...');
      keycloak.updateToken(30).catch(() => {
        console.error('[Keycloak] Failed to refresh expired token');
        logout(); // Logout if refresh fails
      });
    };

    keycloak.onAuthRefreshError = () => {
      console.error('[Keycloak] Auth refresh error - session likely expired');
      logout(); // Logout user when refresh fails
    };

    keycloak.onAuthLogout = () => {
      console.log('[Keycloak] User logged out from another tab/window');
      // Clear local state and redirect to login
      window.location.href = '/login';
    };

    // Set up token refresh interval
    if (authenticated) {
      // Refresh token 5 minutes before it expires
      setInterval(() => {
        keycloak.updateToken(300).catch((error) => {
          console.error('[Keycloak] Failed to refresh token:', error);
          logout(); // Logout instead of forcing login
        });
      }, 60000); // Check every minute
    }

    return authenticated;
  } catch (error) {
    console.error('Failed to initialize Keycloak:', error);
    return false;
  }
};

// Login function
export const login = () => {
  keycloak.login();
};

// Logout function - redirects to ScalePlus logout which clears both ScalePlus and Keycloak sessions
export const logout = () => {
  const appUrl = encodeURIComponent(window.location.origin);
  window.location.href = `https://app.scaleplus.gg/logout?redirect=${appUrl}`;
};

// Get the current access token
export const getToken = (): string | undefined => {
  return keycloak.token;
};

// Get the refresh token (used to get new access tokens)
export const getRefreshToken = (): string | undefined => {
  return keycloak.refreshToken;
};

// Get user roles
export const getUserRoles = (): string[] => {
  return keycloak.realmAccess?.roles || [];
};

// Check if user has specific role
export const hasRole = (role: string): boolean => {
  return getUserRoles().includes(role);
};

// Get user info
export const getUserInfo = () => {
  return keycloak.tokenParsed;
};

// Check if user is authenticated
export const isAuthenticated = (): boolean => {
  return keycloak.authenticated || false;
};

// Refresh token manually using refresh token
// keycloak.updateToken() automatically uses the refresh token to get a new access token
export const refreshToken = async (): Promise<boolean> => {
  try {
    // updateToken(minValidity) checks if token expires in <minValidity seconds
    // If yes, uses refresh_token to get new access_token from Keycloak
    const refreshed = await keycloak.updateToken(5); // Refresh if expires in <5 seconds

    if (refreshed) {
      console.log('[Keycloak] Token refreshed successfully using refresh token');
    } else {
      console.log('[Keycloak] Token still valid, no refresh needed');
    }

    return refreshed;
  } catch (error) {
    console.error('[Keycloak] Failed to refresh token using refresh token:', error);
    return false;
  }
};

export default keycloak;
